import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { EnhancedGeoLocationService } from '@/services/EnhancedGeoLocationService';
import { currencyService } from '@/services/CurrencyService';

interface GuestCurrencyContextType {
  guestCurrency: string | null;
  setGuestCurrency: (currency: string) => void;
  clearGuestCurrency: () => void;
}

const GuestCurrencyContext = createContext<GuestCurrencyContextType | undefined>(undefined);

interface GuestCurrencyProviderProps {
  children: ReactNode;
  shareToken?: string; // Optional share token to namespace the storage
}

export function GuestCurrencyProvider({ children, shareToken }: GuestCurrencyProviderProps) {
  const [guestCurrency, setGuestCurrencyState] = useState<string | null>(null);

  // Create a storage key that includes share token for isolation
  const storageKey = shareToken ? `guest-currency-${shareToken}` : 'guest-currency';

  // Load currency from localStorage on mount, or auto-detect from IP
  useEffect(() => {
    const initializeCurrency = async () => {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          setGuestCurrencyState(saved);
        } else {
          // Auto-detect currency from user's country
          try {
            const countryCode = await EnhancedGeoLocationService.getUserCountry();
            const currencyCode = await currencyService.getCurrencyForCountry(countryCode);
            if (currencyCode && currencyCode !== 'USD') {
              console.log(`🌍 Auto-detected currency: ${currencyCode} for country ${countryCode}`);
              setGuestCurrencyState(currencyCode);
            }
          } catch (error) {
            console.warn('Auto-detection failed, using default:', error);
          }
        }
      } catch (error) {
        console.warn('Failed to load guest currency from localStorage:', error);
      }
    };

    initializeCurrency();
  }, [storageKey]);

  // Set currency and save to localStorage
  const setGuestCurrency = (currency: string) => {
    try {
      localStorage.setItem(storageKey, currency);
      setGuestCurrencyState(currency);
    } catch (error) {
      console.warn('Failed to save guest currency to localStorage:', error);
      // Still update state even if localStorage fails
      setGuestCurrencyState(currency);
    }
  };

  // Clear currency preference
  const clearGuestCurrency = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to clear guest currency from localStorage:', error);
    }
    setGuestCurrencyState(null);
  };

  const value = {
    guestCurrency,
    setGuestCurrency,
    clearGuestCurrency,
  };

  return <GuestCurrencyContext.Provider value={value}>{children}</GuestCurrencyContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGuestCurrency() {
  const context = useContext(GuestCurrencyContext);
  if (context === undefined) {
    throw new Error('useGuestCurrency must be used within a GuestCurrencyProvider');
  }
  return context;
}
